import React, { useEffect, useMemo, useState, useRef } from 'react'
import { Panel } from 'primereact/panel'
import { Button } from 'primereact/button'
import { Menu } from 'primereact/menu'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChargingStation } from '@fortawesome/free-solid-svg-icons'
import { confirmDialog } from 'primereact/confirmdialog'

import SEMANTIC_VERSIONING_REGEX from '../../../common/constants/SemanticVersioning'
import {
    ChargePoint,
    Connector,
    FirmwareGeneralModel,
    KeyValue,
    useChargePointDataGetLatestChargePointDataQuery,
    useChargePointRemoveOwnerMutation,
    useChargePointResetAvailableFirmwareVersionMutation,
    useChargePointResetMutation,
    useFirmwareGetFirmwaresByFilterQuery,
    useUserRemoveFavouriteMutation,
    useUserAddFavouriteMutation,
    useUserIsFavouriteQuery,
    enhancedApi as webApi,
} from '../../../common/redux/api/web-api'
import { compareSemanticVersions, getFirmwareVersion } from '../../../common/services/utilsService'
import { isAdminDomain, isPartnerDomain, isSupportDomain, isUserappDomain } from '../../../lib/currentDomain'
import { downloadManual } from '../../../lib/download-manual'
import findChargingConnector from '../../../lib/findChargingConnector'
import formatDate from '../../../lib/formatDate'
import isAura from '../../../lib/isAura'
import isDawn from '../../../lib/isDawn'
import isOnline from '../../../lib/isOnline'
import useText from '../../../common/hooks/useText'
import { useAppDispatch } from '../../../support/redux/store'
import Text from '../../../ui/Text'
import { MacAdress } from '../../types/MacAdressType'
import isLuna from '../../../lib/isLuna'
import localizeDate from '../../../common/utils/localizeDate'
import { useToast } from '../../../common/uikit/ToastProvider'
import { supportsSimImsi } from '../../../common/utils/chargePointProductUtils'

import { SLoadingProperty } from './charge-point-properties-widget.style'
import { UpgradeFirmwareVersionDialog } from './dialogs/upgrade-firmware-version-dialog'

const LoadingProperty = () => (
    <SLoadingProperty>
        <Text code="msgLoading" />
        ...
    </SLoadingProperty>
)

const localizeWarrantyDate = (utcDate?: string | null): string =>
    !utcDate ? '---' : formatDate(new Date(`${utcDate}${!utcDate.endsWith('Z') ? 'Z' : ''}`), 'yyyy-MM-dd')

export type ChargePointPropertiesWidgetProps = {
    chargePoint: ChargePoint
    disabled?: boolean
    openUnregisterDialog?: (size: string) => void
}

export function ChargePointPropertiesWidget({
    chargePoint,
    disabled,
}: ChargePointPropertiesWidgetProps): React.ReactElement {
    const text = useText()
    const [macAddress, setMacAdress] = useState<MacAdress | undefined>(undefined)
    const [configKeys, setConfigKeys] = useState<KeyValue[]>(chargePoint?.ocppConfigKeys ?? [])
    const [wifiSignalStrength, setWifiSignalStrength] = useState<string>('--')
    const dispatch = useAppDispatch()
    const [removeOwner] = useChargePointRemoveOwnerMutation()
    const [removeChargePointFromFavourite] = useUserRemoveFavouriteMutation()
    const [addChargePointToFavourite] = useUserAddFavouriteMutation()
    const menuRight = useRef<Menu>(null)
    const toast = useToast()
    const chargePointSupportsSimImsi = supportsSimImsi(chargePoint)

    const { data: isFavourite } = useUserIsFavouriteQuery(
        {
            chargePointId: chargePoint.id,
        },
        {
            skip: !chargePoint,
        }
    )
    const toggleFavourite = async () => {
        try {
            if (isFavourite) {
                await removeChargePointFromFavourite({
                    chargePointId: chargePoint.id,
                })
                    .unwrap()
                    .then(() => {
                        toast.notifySuccess(text('lblRemoveChargerFavourites'))
                    })
            } else {
                await addChargePointToFavourite({
                    chargePointId: chargePoint.id,
                })
                    .unwrap()
                    .then(() => {
                        toast.notifySuccess(text('lblAddChargerFavourites'))
                    })
            }
        } catch (exception) {
            toast.notifyError(text('lblErrorUpdateFaviourites'))
        }
    }
    const {
        data: firmwares,
        isLoading: firmwareLoading,
        isSuccess: getFirmwareQuerySuccess,
    } = useFirmwareGetFirmwaresByFilterQuery(
        {
            productId: chargePoint.productId ?? 0,
            firmwareType: 'Master',
            ocppEnabled: chargePoint.isOcpp,
            firmwareStatuses: ['UpdateToAvailable'],
        },
        {
            skip: isAdminDomain || isUserappDomain || chargePoint.productId === undefined,
        }
    )

    const hardwareVersionDescription = chargePoint?.hardwareVersion
    const unregisterOwner = async () => {
        try {
            await removeOwner({
                chargePointId: chargePoint.id,
            })
                .unwrap()
                .then(() => {
                    toast.notifySuccess(text('lblOwnerRemoved'))
                })
        } catch (_) {
            toast.notifyError(text('msgCouldNotRemoveOwner'))
        }
    }

    const [resetAvailableFirmwareVersion] = useChargePointResetAvailableFirmwareVersionMutation()
    const [resetChargePoint] = useChargePointResetMutation()

    const resetFirmwareVersion = async () => {
        try {
            await resetAvailableFirmwareVersion({
                chargePointId: chargePoint.id,
            })
                .unwrap()
                .then(() => {
                    toast.notifySuccess(text('msgAvailableFirmwareVersionWasReset'))
                })
        } catch (_) {
            toast.notifyError(text('msgCouldNotResetFirmwareVersion'))
        }
    }

    const onResetCharger = async () => {
        try {
            await resetChargePoint({ chargePointId: chargePoint.id }).unwrap()
            // invalidates all tags
            dispatch(webApi.util.resetApiState())

            // temporary workaround until we fix the update of electrical settings

            toast.notifySuccess(text('msgChargerResetSuccessfully'))
        } catch (err) {
            toast.notifyError(text('msgChargerResetFailed'))
        }
    }

    const showUpgradeButton = () => {
        if (!isOnline(chargePoint) && chargePoint?.isOcpp) {
            return false
        }

        if (isPartnerDomain && !chargePoint?.isOcpp) {
            return false
        }
        return (isPartnerDomain || isSupportDomain) && availableFirmwares && availableFirmwares?.length > 0
    }

    const showResetButton = () => {
        if (!isOnline(chargePoint) && chargePoint?.isOcpp) {
            return false
        }

        if ((isDawn(chargePoint) || isLuna(chargePoint)) && availableVersion && firmwareVersionConfig) {
            return compareSemanticVersions(availableVersion, firmwareVersionConfig)
        }

        if (chargePoint?.isOcpp && firmwareVersionConfig && availableVersion) {
            return firmwareVersionConfig < availableVersion
        }

        return Boolean(installedVersion && availableVersion && installedVersion < availableVersion)
    }

    const firmwareVersionConfig = useMemo(() => getFirmwareVersion(configKeys), [configKeys])
    const availableVersion = chargePoint?.availableFirmwareVersion
    const installedVersion = chargePoint?.installedFirmwareVersion

    const availableFirmwares = useMemo(() => {
        const filteredFirmwares =
            firmwares?.items &&
            firmwares.items.filter((item: FirmwareGeneralModel) => {
                //check semantic version format  only for dawn(x.y.z)
                const firmwareVersion = item.firmwareVersion
                if (!firmwareVersion) return false

                const filterFirmwaresVersionForDawnLuna =
                    (isDawn(chargePoint) || isLuna(chargePoint)) && firmwareVersion.match(SEMANTIC_VERSIONING_REGEX)

                //filter out chargePoint installed FW version in FW list
                const filteredFirmwareList =
                    (firmwareVersion !== firmwareVersionConfig && firmwareVersionConfig) ||
                    firmwareVersion !== installedVersion

                if (filteredFirmwareList && (!isDawn(chargePoint) || filterFirmwaresVersionForDawnLuna)) {
                    return compareSemanticVersions(
                        firmwareVersion,
                        chargePoint.isOcpp ? firmwareVersionConfig : installedVersion ?? ''
                    )
                }
                return false
            })

        return filteredFirmwares
    }, [chargePoint, firmwares, firmwareVersionConfig, installedVersion])

    const getMenuItems = [
        {
            label: text('btnResetCharger'),
            command: () => {
                resetChargerConfirmation()
            },
            disabled: !isOnline(chargePoint),
            icon: 'pi pi-refresh',
            className: 'auto',
            visible: isSupportDomain,
        },
        {
            label: text('btnDownloadManual'),
            command: () => {
                void downloadManual(chargePoint.id)
            },
            icon: 'pi pi-download',
            visible: !isDawn(chargePoint) && !isLuna(chargePoint),
            disabled: disabled,
        },

        {
            label: text('lblResetFW'),
            command: () => {
                resetFirmwareVersion()
            },
            icon: 'pi pi-power-off',
            visible: isSupportDomain && Boolean(showResetButton()),
            disabled: disabled,
        },
    ]

    const unregisterOwnerConfirmation = () => {
        confirmDialog({
            message: text('msgUnregisterOwnerFromChargepoint'),
            header: text('btnUnregisterOwner'),
            icon: 'pi pi-info-circle',
            defaultFocus: 'accept',
            accept: () => unregisterOwner(),
        })
    }

    const resetChargerConfirmation = () => {
        confirmDialog({
            message: <Text code="msgResetChargerSettingsBody" />,
            header: text('msgResetChargerSettingsTitle'),
            icon: 'pi pi-info-circle',
            defaultFocus: 'accept',
            accept: () => onResetCharger(),
        })
    }
    const chargerOfflineTimeDate = useMemo(
        () => (chargePoint.isOcpp ? chargePoint.offlineSince : chargePoint.lastHeartbeat),
        [chargePoint]
    )
    const {
        data: macAddressRequest,
        isLoading,
        isError: macAddressLoadError,
    } = useChargePointDataGetLatestChargePointDataQuery({
        chargePointId: chargePoint.id,
        dataTypeId: 8,
    })

    useEffect(() => {
        const macAndIp = (macAddressRequest?.formattedData as string) || '--;--'
        const splitted = macAndIp.split(';')
        setMacAdress({
            macAddress: splitted[0],
            localIp: splitted[1],
        })
    }, [macAddressRequest])

    useEffect(() => {
        if (chargePoint && chargePoint?.isOcpp) {
            setConfigKeys(chargePoint?.ocppConfigKeys ?? [])
        }
    }, [chargePoint])

    useEffect(() => {
        if (macAddressLoadError) {
            console.error('Could not get mac address')
        }
    }, [macAddressLoadError])

    const [isUpgradeFirmwareVersionDialogVisible, setIsUpgradeFirmwareVersionDialogVisible] = useState(false)

    const { data: chargePointDataLatestItem, isLoading: wifiSignalStrengthLoading } =
        useChargePointDataGetLatestChargePointDataQuery({
            chargePointId: chargePoint.id,
            dataTypeId: 1,
        })

    useEffect(() => {
        const signalStrength = chargePointDataLatestItem?.formattedData

        setWifiSignalStrength(
            !signalStrength
                ? '--'
                : chargePoint.chargePointStatus === 'Offline'
                  ? `(${signalStrength}) ${text('lblDecibelShort')}`
                  : `${signalStrength} ${text('lblDecibelShort')}`
        )
    }, [chargePoint.chargePointStatus, chargePointDataLatestItem, text])

    const getConnectorsState = (stateType: 'factoryMaxPower' | 'installationMaxPower' | 'evConnectorState') => {
        if (chargePoint.productId === undefined) {
            return null
        }

        const isPowerState = stateType === 'factoryMaxPower' || stateType === 'installationMaxPower'
        if (isAura(chargePoint)) {
            const leftConnectorValue = chargePoint.connectors?.find((connector) => connector.connectorId === 1)?.[
                stateType
            ]
            const rightConnectorValue = chargePoint.connectors?.find((connector) => connector.connectorId === 2)?.[
                stateType
            ]

            if (!leftConnectorValue || !rightConnectorValue) return null

            return isPowerState ? (
                <Text
                    code="lblConnectorsPowerValue"
                    values={{ leftConnectorValue: leftConnectorValue, rightConnectorValue: rightConnectorValue }}
                />
            ) : (
                <Text
                    code="lblConnectorsStateValue"
                    values={{ leftConnectorValue: leftConnectorValue, rightConnectorValue: rightConnectorValue }}
                />
            )
        }

        const chargingConnector = findChargingConnector(chargePoint.connectors ?? [])?.[stateType]
        if (!chargingConnector) return null

        return chargingConnector + (isPowerState ? ` ${text('lblkW')}` : '')
    }

    const renderConnectorInformation = (connector: Connector) => {
        const socketSide = connector?.connectorId === 1 ? 'left' : 'right'
        const connectorName = connector?.connectorId === 1 ? text('lblLeftConnector') : text('lblRightConnector')

        if (!connector?.connectorUserName) return
        if (connector) {
            return (
                <div className="grid ml-3" key={socketSide}>
                    <div className="flex flex-column gap-3 col-6" key={`socket-owner-${connector?.connectorId}`}>
                        <div className="text-700" data-testid={`${socketSide}SocketOwner`}>
                            <div>{connectorName}</div>
                            <Text code={'lblOwner'} />:
                        </div>
                        <div data-testid={`${socketSide}SocketOwner-value`}>{connector.connectorUserName} </div>
                    </div>
                    <div className="flex flex-column gap-3 col-6" key={`socket-email-${connector.connectorId}`}>
                        <div className="text-700" data-testid={`${socketSide}SocketEmail`}>
                            <div>{connectorName}</div> <Text code="lblOwnerEmail" />:
                        </div>
                        <div data-testid={`${socketSide}SocketEmail-value`}>{connector.connectorUserEmail} </div>
                    </div>
                    <div className="flex flex-column gap-3 col-6" key={`socket-mobile-${connector.connectorId}`}>
                        <div className="text-700" data-testid={`${socketSide}SocketMobile`}>
                            <div> {connectorName}</div> <Text code="lblOwnerMobile" />:
                        </div>
                        <div data-testid={`${socketSide}SocketMobile-value`}>{connector.connectorUserMobile} </div>
                    </div>
                </div>
            )
        }
    }

    const ownerProperties = (
        <Panel
            header={
                <span className="flex justify-content-center gap-2">
                    <i className="pi pi-user"></i>
                    <span>{text('lblOwnerProperties')}</span>
                </span>
            }
            toggleable
            data-testid="owner-properties-widget"
        >
            {chargePoint.connectors &&
                chargePoint.connectors.length &&
                chargePoint.connectors.map((connector) => renderConnectorInformation(connector))}
            <div className="grid">
                {chargePoint.connectors &&
                    !chargePoint.connectors[0]?.connectorUserName &&
                    !chargePoint.connectors[1]?.connectorUserName && (
                        <div className="flex flex-column gap-3 col-6">
                            <div className="text-700" data-testid="owner">
                                <Text code="lblOwner" />
                            </div>

                            <div className="flex flex-wrap">
                                <span>{chargePoint.ownerName || <span data-testid="owner-name">--</span>}</span>
                                {chargePoint.ownerId && !disabled && (
                                    <Button
                                        icon="pi pi-user-minus"
                                        outlined
                                        text
                                        severity="info"
                                        onClick={unregisterOwnerConfirmation}
                                        data-testid="unregister"
                                        className=" text-blue-500 pointer p-0"
                                        title={text('btnUnregisterOwner')}
                                    ></Button>
                                )}
                            </div>
                        </div>
                    )}
                {chargePoint.ownerMobile && (
                    <div className="flex flex-column gap-3 col-6">
                        <div className="text-700" data-testid="mobile-field">
                            <Text code="lblOwnerMobile" />
                        </div>
                        <div data-testid="mobile-value" className="mt-2">
                            {chargePoint.ownerMobile || <span data-testid="mobile">--</span>}
                        </div>
                    </div>
                )}
                {chargePoint.ownerEmail && (
                    <div className="flex flex-column gap-3 col-6 flex-wrap">
                        <div className="text-700" data-testid="email-field">
                            <Text code="lblOwnerEmail" />
                        </div>
                        <div data-testid="email-value">
                            {chargePoint.ownerEmail || <span data-testid="email">--</span>}
                        </div>
                    </div>
                )}

                {chargePoint.companyName && !isAdminDomain && (
                    <div className="flex flex-column gap-3 col-6">
                        <div className="text-700" data-testid="company">
                            <Text code="lblOrganisation" />
                        </div>
                        <div className="flex gap-2 ">
                            {chargePoint.companyName}
                            <Link
                                to={`/company/${chargePoint.companyId}`}
                                data-testid="go-to-company"
                                title={text('btnGoToOrganisation')}
                                className="pi pi-external-link mt-1 text-sm mr-4"
                            ></Link>
                        </div>
                    </div>
                )}

                {chargePoint.poolName && (
                    <div className="flex flex-column gap-3 col-6">
                        <div className="text-700" data-testid="pool">
                            <Text code="lblPool" />
                        </div>
                        <div data-testid="pool-value">
                            {chargePoint.poolName || <span data-testid="spool">--</span>}
                        </div>
                    </div>
                )}

                {chargePoint.loadBalancerGroupName && (
                    <div className="flex flex-column gap-3 col-6">
                        <div className="text-700" data-testid="lb">
                            <Text code="lblLoadbalancerGroup" />
                        </div>
                        <div className="flex gap-2">
                            {chargePoint.loadBalancerGroupName}
                            <Link
                                className="pi pi-external-link mt-1 text-sm mr-4"
                                to={`/loadbalancer/${chargePoint.loadBalancerGroupId}`}
                                data-testid="lb-redirect"
                                title={text('lblGoLoadBalancerGroup')}
                            ></Link>
                        </div>
                    </div>
                )}
            </div>
        </Panel>
    )

    const settings = (
        <Panel
            header={
                <span className="flex justify-content-center gap-2">
                    <i className="pi pi-microchip" />

                    <span>{text('lblHardwareProperties')}</span>
                </span>
            }
            toggleable
            data-testid="hardware-properties-widget"
        >
            <div className="grid">
                <div className="flex flex-column gap-3 col-6">
                    <div data-testid="cu-serial-no" className="text-700">
                        {isDawn(chargePoint) ? text('lblCuSerialNumber') : text('lblSerialNumber')}
                    </div>
                    {isDawn(chargePoint) && chargePoint?.chargingUnitSerialNumber
                        ? chargePoint?.chargingUnitSerialNumber
                        : chargePoint.id}
                </div>
                {isDawn(chargePoint) && (
                    <div className="flex flex-column gap-3 col-6">
                        <div data-testid="backplate-serial-no" className="text-700">
                            <Text code="lblBackplateSerialNumber" />
                        </div>
                        {chargePoint.serialNumber ?? <span data-testid="backplate-serial-number">--</span>}
                    </div>
                )}
                <div className="flex flex-column gap-3 col-6">
                    <div data-testid="hardware-version-status" className="text-700">
                        <Text code="lblHardwareVersion" />
                    </div>
                    {(hardwareVersionDescription && (
                        <div className="flex gap-1">
                            <span>{hardwareVersionDescription}</span>
                        </div>
                    )) || <span data-testid="hardware-version">--</span>}
                </div>

                {!isAdminDomain && (
                    <>
                        <div className="flex flex-column gap-3 col-6">
                            <div data-testid="fw-version" className="text-700">
                                <Text code="lblFirmwareVersion" />
                            </div>

                            <div data-testid="fw-value" className="flex gap-2">
                                {chargePoint?.isOcpp
                                    ? firmwareVersionConfig
                                    : chargePoint?.installedFirmwareVersion ?? 0}
                                {showUpgradeButton() ? (
                                    <i
                                        onClick={() => setIsUpgradeFirmwareVersionDialogVisible(true)}
                                        data-testid="upgrade-fw"
                                        title={text('lblUpgradeFWVersion')}
                                        style={{ fontSize: '1.2rem' }}
                                        className="pi pi-arrow-circle-up text-blue-500 pointer"
                                    ></i>
                                ) : null}
                            </div>
                        </div>
                    </>
                )}

                {!chargePoint?.isOcpp && (
                    <>
                        <div className="flex flex-column gap-3 col-6">
                            <div className="text-700" data-testid="fmaxpower">
                                <Text code="lblFactoryMaxPower" />
                            </div>
                            <div data-testid="fmaxpower-value">
                                {getConnectorsState('factoryMaxPower') || (
                                    <span data-testid="factory-max-power">--</span>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-column gap-3 col-6">
                            <div className="text-700" data-testid="imaxpower">
                                <Text code="lblInstallationMaxPower" />
                            </div>
                            <div data-testid="imaxpower-value">
                                {getConnectorsState('installationMaxPower') || (
                                    <span data-testid="installation-max-power">--</span>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Panel>
    )

    const networkProperties = (
        <Panel
            header={
                <span className="flex justify-content-center gap-2">
                    <i className="pi pi-wifi" />
                    <span>{text('lblNetworkProperties')}</span>
                </span>
            }
            toggleable
            data-testid="network-properties-widget"
        >
            <div className="grid">
                {chargePointSupportsSimImsi && (
                    <div className="flex flex-column gap-3 col-6">
                        <div data-testid="ImsiNumber" className="text-700">
                            <Text code="lblSimImsiNumber" />
                        </div>
                        <div data-testid="wifi-value">
                            {chargePoint?.imsiNumber || <span data-testid="imsiNumber">--</span>}
                        </div>
                    </div>
                )}

                {!chargePoint.isOcpp && (
                    <>
                        <div className="flex flex-column gap-3 col-6">
                            <div data-testid="address" className="text-700">
                                <Text code="lblIPAddress" />
                            </div>
                            <div data-testid="address-value">
                                {chargePoint.ip || <span data-testid="ip-address">--</span>}
                            </div>
                        </div>

                        <div className="flex flex-column gap-3 col-6">
                            <div data-testid="wifi" className="text-700">
                                <Text code="lblWiFiSignalStrength" />
                            </div>
                            <div data-testid="wifi-value">
                                {wifiSignalStrengthLoading ? <LoadingProperty /> : wifiSignalStrength}
                            </div>
                        </div>
                    </>
                )}

                {macAddressRequest?.formattedData && (
                    <div className="flex flex-column gap-3 col-6">
                        <div data-testid="ipAddress" className="text-700">
                            <Text code="lblLocalIPAddress" />
                        </div>
                        <div data-testid="ipAddress-value">{macAddress?.localIp}</div>
                    </div>
                )}
                {macAddressRequest?.formattedData && (
                    <div className="flex flex-column gap-3 col-6">
                        <div data-testid="macAddress" className="text-700">
                            <Text code="lblMacAddress" />
                        </div>
                        <div data-testid="macAddress-value">
                            {isLoading ? <LoadingProperty /> : macAddress?.macAddress || '--'}
                        </div>
                    </div>
                )}
            </div>
        </Panel>
    )

    const warrantyProperties = (
        <>
            <div className="flex flex-column gap-3 col-6">
                <div data-testid="base-warranty-ends" className="text-700">
                    {text('lblBaseWarrantyEnds')}
                </div>
                <div data-testid="base-warranty-ends-value">
                    {localizeWarrantyDate(chargePoint.warrantyInformation?.baseWarrantyEndsAtUtc)}
                </div>
            </div>
            <div className="flex flex-column gap-3 col-6">
                <div data-testid="extended-warranty-ends" className="text-700">
                    {text('lblExtendedWarrantyEnds')}
                </div>
                <div data-testid="extended-warranty-ends-value">
                    {localizeWarrantyDate(chargePoint.warrantyInformation?.extendedWarrantyEndsAtUtc)}
                </div>
            </div>
        </>
    )

    return (
        <div className="grid" data-testid="properties">
            <span className="col-12">
                <Panel
                    header={
                        <span className="flex justify-content-center gap-2">
                            <FontAwesomeIcon icon={faChargingStation} />
                            <span>{text('lblChargerProperties')}</span>
                        </span>
                    }
                    icons={
                        <>
                            {!isAdminDomain && (
                                <Button
                                    size="small"
                                    className="mr-2"
                                    data-testid="toggle-fav"
                                    onClick={toggleFavourite}
                                    outlined
                                    icon={
                                        isFavourite ? (
                                            <i className="pi pi-star-fill" style={{ color: '#22c55e' }}></i>
                                        ) : (
                                            'pi pi-star'
                                        )
                                    }
                                    title={isFavourite ? text('lblAddedToFavourites') : text('lblAddFavourites')}
                                ></Button>
                            )}
                            {isSupportDomain && (
                                <>
                                    <Menu
                                        model={getMenuItems}
                                        popup
                                        ref={menuRight}
                                        id="popup_menu_right"
                                        popupAlignment="right"
                                        className="mt-1"
                                    />
                                    <Button
                                        size="small"
                                        outlined
                                        icon="pi pi-bars"
                                        onClick={(event) => menuRight.current?.toggle(event)}
                                        aria-controls="popup_menu_right"
                                        aria-haspopup
                                        className="p-button-plain"
                                        data-testid="menu-button"
                                    />
                                </>
                            )}
                        </>
                    }
                    data-testid="charge-point-properties-widget"
                >
                    <div className="grid">
                        <div className="flex flex-column gap-3 col-6">
                            <div data-testid="serial-no" className="text-700">
                                <Text code="lblChargePointId" />
                            </div>
                            <div data-testid="chargePointId-value">{chargePoint.id}</div>
                        </div>
                        <div className="flex flex-column gap-3 col-6">
                            <div data-testid="wifi-password" className="text-700">
                                <Text code="lblPin" />
                            </div>
                            <div data-testid="wifiPassword-value">{chargePoint.wifiPassword}</div>
                        </div>
                        {isSupportDomain ? warrantyProperties : null}

                        {!isOnline(chargePoint) && chargerOfflineTimeDate && (
                            <div className="flex flex-column gap-3 col-6">
                                <div data-testid={`lastHeartbeat-${chargePoint.id}`} className="text-700">
                                    <Text code="lblLastSeenTime" />
                                </div>
                                <div data-testid="lblLastSeenTime-value">{localizeDate(chargerOfflineTimeDate)} </div>
                            </div>
                        )}
                    </div>
                </Panel>
                <div className="mt-3">{settings}</div>

                {(macAddressRequest?.formattedData || !chargePoint.isOcpp || chargePointSupportsSimImsi) && (
                    <div className="mt-3">{networkProperties}</div>
                )}

                <div className="mt-3">{ownerProperties}</div>
            </span>

            <UpgradeFirmwareVersionDialog
                visible={isUpgradeFirmwareVersionDialogVisible}
                availableFirmwares={availableFirmwares}
                firmwareLoading={firmwareLoading}
                getFirmwareQuerySuccess={getFirmwareQuerySuccess}
                onHide={() => setIsUpgradeFirmwareVersionDialogVisible(false)}
                chargePoint={chargePoint}
            />
        </div>
    )
}
